import React from 'react';
import { Box, Container, Typography, Button, Grid, Link as MuiLink } from '@mui/material';
import { Star, CheckCircle, CancelOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES, VOVER_LANDING_URL } from '@/constants';
import { LANDING_CONTAINER_MAX_WIDTH, landingFooterSpacing, landingSectionSpacing } from '@/constants/landingLayout';
import StoreBadgeButton from '@components/StoreBadgeButton';
import { openGooglePlayListing } from '@utils/openGooglePlayListing';
import { openAppStoreListing } from '@utils/openAppStoreListing';

type CondoFinalCtaSectionProps = {
  defaultTrialDays: number;
  onNavigate: (path: string) => void;
};

export default function CondoFinalCtaSection({ defaultTrialDays, onNavigate }: CondoFinalCtaSectionProps) {
  return (
    <>
      {/* CTA Section Final - mesmo padrão de fundo da tela de login */}
      <Box
        sx={{
          ...landingSectionSpacing,
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
          color: 'text.primary',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH} sx={{ position: 'relative', zIndex: 1 }}>
          {/* Badge de destaque */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'rgba(33, 49, 106, 0.08)',
              px: 4,
              py: 1.5,
              borderRadius: 4,
              mb: 4,
              border: '2px solid rgba(33, 49, 106, 0.2)',
              boxShadow: '0 4px 20px rgba(33, 49, 106, 0.08)',
            }}
          >
            <Star sx={{ fontSize: 24, color: '#21316a' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' }, color: '#21316a' }}>
              {defaultTrialDays} {defaultTrialDays === 1 ? 'Dia' : 'Dias'} Grátis
            </Typography>
            <Star sx={{ fontSize: 24, color: '#21316a' }} />
          </Box>

          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 3,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              lineHeight: 1.2,
              color: '#21316a',
            }}
          >
            Pronto para transformar sua gestão de demandas?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
              fontSize: { xs: '1rem', md: '1.3rem' },
              fontWeight: 400,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              color: 'text.secondary',
            }}
          >
            {`Junte-se a empresas que já estão otimizando seus processos com a VOVER.

            Comece seu teste gratuito hoje e descubra como podemos ajudar você.`}
          </Typography>

          {/* Grid de benefícios */}
          <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center' }}>
            <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: '600px' }}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid rgba(33, 49, 106, 0.15)',
                    textAlign: 'center',
                  }}
                >
                  <CheckCircle sx={{ fontSize: 40, mb: 1, color: '#21316a' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem', color: 'text.secondary' }}>
                    Acesso completo
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid rgba(33, 49, 106, 0.15)',
                    textAlign: 'center',
                  }}
                >
                  <CancelOutlined sx={{ fontSize: 40, mb: 1, color: '#21316a' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem', color: 'text.secondary' }}>
                    Cancele quando quiser
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* CTA Principal */}
          <Button
            variant="contained"
            size="large"
            onClick={() => onNavigate(`${ROUTES.LOGIN}?tab=signup`)}
            sx={{
              backgroundColor: '#21316a',
              color: 'white',
              px: { xs: 5, md: 8 },
              py: { xs: 2, md: 2.5 },
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              fontWeight: 'bold',
              boxShadow: '0 8px 32px rgba(33, 49, 106, 0.25)',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#1a2854',
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 40px rgba(33, 49, 106, 0.35)',
              },
            }}
          >
            Criar Conta Grátis Agora
          </Button>

          {/* Badges de lojas: Google Play e App Store */}
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <StoreBadgeButton type="google-play" onClick={() => openGooglePlayListing()} aria-label="Baixar no Google Play" />
            <StoreBadgeButton type="app-store" onClick={() => openAppStoreListing()} aria-label="Baixar na App Store" />
          </Box>

          {/* Texto de garantia */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, mb: 1, color: 'text.secondary' }}>
              ✓ Configuração em menos de 5 minutos
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, mb: 1, color: 'text.secondary' }}>
              ✓ Suporte técnico incluso durante o período de teste
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, color: 'text.secondary' }}>
              ✓ Sem compromisso • Sem fidelidade • Cancele a qualquer momento
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          ...landingFooterSpacing,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <MuiLink href={VOVER_LANDING_URL} underline="hover" color="text.secondary" sx={{ fontWeight: 600 }}>
                Site VOVER
              </MuiLink>
              <MuiLink component={RouterLink} to={ROUTES.CONTACT} underline="hover" color="text.secondary" sx={{ fontWeight: 600 }}>
                Contato
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to={ROUTES.PRIVACY_POLICY}
                underline="hover"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Política de Privacidade
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to={ROUTES.ACCOUNT_DELETION_INFO}
                underline="hover"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Exclusão de conta
              </MuiLink>
            </Box>
            <Typography variant="body2" align="center" color="text.secondary">
              © {new Date().getFullYear()} V-Condo. Todos os direitos reservados. Um produto VOVER Tecnologia e Serviços LTDA.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
}
